import includes from 'lodash/includes'
import isEqual from 'lodash/isEqual'
import { action, computed, makeObservable, observable } from 'mobx'

import { TentacleError } from 'modules/core/lib/errors'

import type { Origin } from 'modules/reports/lib/OriginStruct'
import ReportForm from 'modules/reports/lib/ReportForm'
import ReportStruct from 'modules/reports/lib/ReportStruct'
import dashboards from 'modules/reports/store/collections/dashboards'
import fields from 'modules/reports/store/collections/fields'
import Report from 'modules/reports/store/models/Report'
import type { SaveParams, SqlConfiguration, Variable } from 'modules/reports/store/models/Report'
import { ResourceData } from 'modules/reports/store/models/Resource'

import FilterFormCollection from './FormCollections/Filter'
import type FilterForm from './FormCollections/Filter/Form'
import ProjectionFormCollection from './FormCollections/Projection/ProjectionFormCollection'
import getDefaultProjections from './defaultProjections'

interface Params {
  report: Report
  origin?: Origin
}

function withDefaultProjections(projection: ProjectionFormCollection, reportForm: ReportForm) {
  const projections = projection.convertToParams()
  const defaultProjections = getDefaultProjections(reportForm)
  defaultProjections.forEach((defaultProjection: any) => {
    projections.push(defaultProjection)
  })

  return projections
}

export class ReportEditorStore {
  loading = false
  report: Report
  saved: boolean
  reportForm: ReportForm
  projection: ProjectionFormCollection
  filters: FilterFormCollection<FilterForm>
  variables: Variable[]
  originalSerializedValue: SaveParams
  sqlQuery: string | null | undefined
  sqlConfiguration: SqlConfiguration | null | undefined
  origin?: Origin

  // Execution results. Used by the ReportNameModal to prevent saving on error.
  // For SQL, the results and specific error is also stored here
  builderReportHadErrors: boolean
  sqlQueryExecuted: boolean
  sqlQueryResult?: ResourceData
  sqlQueryError?: TentacleError

  constructor(params: Params) {
    makeObservable(this, {
      loading: observable,
      report: observable,
      saved: observable,
      projection: observable,
      filters: observable,
      sqlQuery: observable,
      sqlQueryExecuted: observable,
      sqlQueryResult: observable,
      sqlQueryError: observable,
      variables: observable,
      origin: observable,
      start: action,
      setSqlQuery: action,
      setSqlQueryResult: action,
      setSqlQueryError: action,
      setBuilderResult: action,
      reportStruct: computed,
      isDirty: computed,
    })
    this.report = params.report
    this.origin = params.origin || params.report.origin
    this.saved = false
    this.projection = new ProjectionFormCollection({
      projections: this.report.projections.map((p) => p.serialize()),
    })
    this.filters = new FilterFormCollection({
      filters: this.report.filters.map((f) => f.serialize()),
    })

    const dashboard =
      this.report.has('dashboard_id') && dashboards.get(+this.report.attributes.dashboard_id)
    this.variables =
      (dashboard && dashboard.has('variables') && dashboard.attributes.variables) || []

    const formPayload = this.report.toJS()
    this.reportForm = new ReportForm({
      ...formPayload,
      ...formPayload.sql_configuration,
    })
    this.sqlQuery = this.report.sqlQuery
    this.builderReportHadErrors = false
    this.sqlQueryExecuted = false
    this.sqlConfiguration = this.report.sqlConfiguration
    this.originalSerializedValue = this.serializeForSave()
  }

  async start() {
    this.loading = true
    fields.isEmpty && (await fields.fetch())
    this.loading = false
  }

  setSqlQuery(newSqlQuery: string) {
    if (this.sqlQuery !== newSqlQuery) {
      this.sqlQuery = newSqlQuery
      this.sqlQueryExecuted = false
    }
  }

  setSqlQueryResult(sqlQuery: string, sqlQueryResult: ResourceData) {
    if (this.sqlQuery === sqlQuery) {
      this.sqlQueryResult = sqlQueryResult
      this.sqlQueryError = undefined
      this.sqlQueryExecuted = true
    }
  }

  setSqlQueryError(sqlQuery: string, error: TentacleError) {
    if (this.sqlQuery === sqlQuery) {
      this.sqlQueryResult = undefined
      this.sqlQueryError = error
      this.sqlQueryExecuted = true
    }
  }

  setBuilderResult(error: boolean) {
    this.builderReportHadErrors = error
  }

  get reportStruct(): ReportStruct<Report> {
    return new ReportStruct({
      resource: new Report({
        chart_type: this.reportForm.get('chart_type').value,
      }),
      projections: this.sqlQuery ? [] : withDefaultProjections(this.projection, this.reportForm),
      filters: this.sqlQuery ? [] : this.filters.convertToParams(),
      sqlQuery: this.sqlQuery,
      sqlConfiguration: this.sqlConfiguration,
      shares: [],
    })
  }

  serializeForSave(origin?: Origin): any {
    const data = this.reportForm.data()
    const serialized = this.reportStruct.serialize()
    const params: Record<string, any> = {
      ...serialized,
      report: {
        id: data.id,
        title: data.title,
        type: data.chart_type,
        dashboard_id: data.dashboard_id,
        origin: origin || this.origin,
      },
    }

    if (serialized.sql_query) {
      params.sql_configuration = this.buildSqlConfiguration(data)
    }

    return params
  }

  get isDirty() {
    return !isEqual(this.originalSerializedValue, this.serializeForSave())
  }

  isFieldEnabled(formIndex: number): boolean {
    if (formIndex === 0) {
      return true
    }

    return !!(this.projection.forms?.length && this.projection.forms[formIndex - 1])
  }

  buildSqlConfiguration(data: any): SqlConfiguration | null | undefined {
    if (data.chart_type === Report.CHART_TYPES.table) {
      return null
    }

    return {
      initial_data: data.initial_data,
      secondary_data: data.secondary_data,
      tertiary_data: data.tertiary_data,
    }
  }

  isFiltersFieldEnabled(): boolean {
    const needOnlyOne = includes(
      [Report.CHART_TYPES.table, Report.CHART_TYPES.numeric_metric],
      this.reportForm.get('chart_type').value
    )

    if (needOnlyOne && this.projection.forms?.length === 1) {
      return true
    }

    if (this.projection.forms && this.projection.forms.length >= 2) {
      return true
    }

    return false
  }

  /**
   * When changing chart type we reset projection forms
   */
  resetForms(prevChartType: string) {
    const nextChartType = this.reportForm.get('chart_type').value

    // Both table and scatterplot are incompatible with other reports at projection level,
    // either when selecting it or deselecting it
    if (
      [prevChartType, nextChartType].includes(Report.CHART_TYPES.table) ||
      [prevChartType, nextChartType].includes(Report.CHART_TYPES.scatterplot)
    ) {
      return this.projection.resetForms()
    }
  }
}
